import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Toolbar,
  Typography,
  styled,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import WhatsappFlowNode from "./WhatsappFlowNode";
import WhatsappFlowConditionalMessagesSection from "./WhatsappFlowConditionalMessagesSection";
import WhatsappFlowNotificationsSection from "./WhatsappFlowNotificationsSection";
import { validateAllFlowConditionals } from "./whatsappFlowConditional";
import WhatsappFlowStepsEditionSection from "./WhatsappFlowStepsEditionSection";
import {
  getWhatsappMessageTypes,
  getWhatsappMessageParameters,
  getWhatsappValidationTypes,
  getInvalidMessagePlaceholders,
  getConditionalFieldOptions,
  extractPlaceholdersFromMessage,
  TEMPLATE_TEXT,
  TEMPLATE_BUTTONS,
  TEMPLATE_WHATSAPP,
  TEMPLATE_DYNAMIC,
  TEMPLATE_FLOW,
  MAX_WHATSAPP_REPLY_BUTTONS,
  validationTypeMeta,
  VALIDATION_VALUE_KIND,
  saveVariableOptions,
  availableActions,
  userOptions,
  templateLanguageOptions,
  VALIDATION_TO_SPECIAL_VARIABLE,
  MANUAL_SPECIAL_VARIABLE_KEYS,
  SPECIAL_VARIABLE_META,
  ANSWER_TO_OPTIONS_VAR,
  missingRequiredValidationForSaveVariable,
  FLOW_ROLE_OPTIONS,
  QUOTA_OPTIONS_VARIABLE,
  stepUsesQuotaOptionsPlaceholder,
  CURRENT_BALANCE_VARIABLE,
  stepUsesCurrentBalancePlaceholder,
  MAX_APPROVED_EXTENSION_VARIABLE,
  stepUsesMaxApprovedExtensionPlaceholder,
  EXTENSION_QUOTA_OPTIONS_VARIABLE,
  stepUsesExtensionQuotaOptionsPlaceholder,
  PAGARE_URL_TOKEN,
  stepMissingPagareValidationForUrl,
  stepHasVideollamadaValidation,
} from "./whatsappFlowCatalogs";
import {
  getAgeRangeValidationError,
  getFlowAgeRangeValidationError,
  getFlowFraudValidationError,
  getOptionsTokensFromRules,
  splitFraudValidationValue,
} from "./whatsappFlowValidationCodec";
import { syncOptionBranches } from "./whatsappFlowOptionBranches";
import {
  buildWhatsappFlowPayload,
  createWhatsappFlowNode,
  normalizeWhatsappFlowNode,
  prepareStepConnectionPayload,
} from "./whatsappFlowStorage";
import { getStepConnectionByUuid, createStepConnection, updateStepConnection, getPublicHouseTypes, getPublicGenders, getPublicMaritalStatuses, getPublicOccupations, getPublicSectorOccupations } from "./whatsappFlowApi";

const nodeTypes = {
  whatsappStep: WhatsappFlowNode,
};

const initialNodes = [createWhatsappFlowNode("step-1", 1, { x: 80, y: 120 })];

const isExperianDecisorPrefilterYes = (value) => {
  if (value == null || String(value).trim() === "") return false;
  const s = String(value).trim().toLowerCase();
  if (s === "prefilter=true") return true;
  if (s === "prefilter=false") return false;
  return /prefilter\s*=\s*true/.test(s);
};

/** Valor almacenado `a%b%c` ↔ filas separadas en el formulario (el usuario no escribe `%`). */
const optionsRowsFromRuleValue = (value) => {
  if (value == null) return [""];
  const s = String(value);
  if (s === "") return [""];
  return s.split("%");
};

const optionsRuleValueFromRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  return rows.map((r) => String(r)).join("%");
};

const HOUSE_TYPE_OPTIONS_TOKEN = "{{house_type_options}}";
const OCCUPATION_OPTIONS_TOKEN = "{{occupation_options}}";
const SECTOR_OCCUPATION_OPTIONS_TOKEN = "{{sector_occupation_options}}";
const GENDER_OPTIONS_TOKEN = "{{gender_options}}";
const MARITAL_STATUS_OPTIONS_TOKEN = "{{estado_civil_options}}";

const stepUsesHouseTypeOptions = (node) => {
  const message = node?.data?.message || "";
  const keys = node?.data?.parameterKeys || [];
  const rules = node?.data?.validationRules || [];
  const hasValidation = rules.some((r) => r?.type === "HOUSE_TYPE_INDEX_ALLOWED");
  const hasVariable =
    message.includes(HOUSE_TYPE_OPTIONS_TOKEN) || keys.includes(HOUSE_TYPE_OPTIONS_TOKEN);
  return hasValidation || hasVariable;
};

const stepUsesOccupationOptions = (node) => {
  const message = node?.data?.message || "";
  const keys = node?.data?.parameterKeys || [];
  const rules = node?.data?.validationRules || [];
  const hasValidation = rules.some((r) => r?.type === "OCCUPATION_INDEX_ALLOWED");
  const hasVariable =
    message.includes(OCCUPATION_OPTIONS_TOKEN) || keys.includes(OCCUPATION_OPTIONS_TOKEN);
  return hasValidation || hasVariable;
};

const stepUsesSectorOccupationOptions = (node) => {
  const message = node?.data?.message || "";
  const keys = node?.data?.parameterKeys || [];
  const rules = node?.data?.validationRules || [];
  const hasValidation = rules.some((r) => r?.type === "SECTOR_OCCUPATION_INDEX_ALLOWED");
  const hasVariable =
    message.includes(SECTOR_OCCUPATION_OPTIONS_TOKEN) ||
    keys.includes(SECTOR_OCCUPATION_OPTIONS_TOKEN);
  return hasValidation || hasVariable;
};

const stepUsesGenderOptions = (node) => {
  const message = node?.data?.message || "";
  const keys = node?.data?.parameterKeys || [];
  const rules = node?.data?.validationRules || [];
  return (
    rules.some((r) => r?.type === "GENDER_INDEX_ALLOWED") ||
    message.includes(GENDER_OPTIONS_TOKEN) ||
    keys.includes(GENDER_OPTIONS_TOKEN)
  );
};

const stepUsesMaritalStatusOptions = (node) => {
  const message = node?.data?.message || "";
  const keys = node?.data?.parameterKeys || [];
  const rules = node?.data?.validationRules || [];
  return (
    rules.some((r) => r?.type === "MARITAL_STATUS_INDEX_ALLOWED") ||
    message.includes(MARITAL_STATUS_OPTIONS_TOKEN) ||
    keys.includes(MARITAL_STATUS_OPTIONS_TOKEN)
  );
};

/**
 * Carga perezosa (una sola vez) de un catálogo público cuando el paso seleccionado lo usa.
 */
const useLazyCatalog = (selectedNode, usesFn, fetcher, errorFallback) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!selectedNode || !usesFn(selectedNode)) return;
    if (fetchedRef.current) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (!isMounted) return;
        setItems(result);
        fetchedRef.current = true;
      })
      .catch((err) => {
        if (!isMounted) return;
        setItems([]);
        setError(err?.response?.data?.message || err?.message || errorFallback);
        fetchedRef.current = true;
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode]);

  return { items, loading, error };
};

const formatNumberedList = (items) =>
  items.map((label, index) => `${index + 1}. ${label}`).join(", ");

const ButtonPrincipal = styled(Button)({
  borderRadius: "20px",
  backgroundColor: "#161e55",
  color: "#ffff",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#A8C8AF",
  },
});

const ButtonSecondary = styled(Button)({
  borderRadius: "20px",
  color: "#161e55",
  borderColor: "#161e55",
  textTransform: "none",
});

const Panel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: "18px",
  height: "100%",
  minHeight: "68vh",
  boxSizing: "border-box",
}));

/** Misma referencia visual que Scores / lista de flujos: deja espacio bajo el AppBar fijo */
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

export default function FlujosWhatsappEditor() {
  const { flowUuid: flowUuidParam } = useParams();
  const flowUuid =
    flowUuidParam && flowUuidParam !== "nuevo" ? flowUuidParam : undefined;
  const navigate = useNavigate();
  const isNew = !flowUuid;

  const [flowName, setFlowName] = useState("Flujo WhatsApp");
  const [flowDescription, setFlowDescription] = useState("");
  const [flowRole, setFlowRole] = useState("PRIMARY");
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const edgesRef = useRef(edges);
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);
  const [selectedNodeId, setSelectedNodeId] = useState(initialNodes[0].id);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [messageTypes, setMessageTypes] = useState([]);
  const [parameterOptions, setParameterOptions] = useState([]);
  const conditionalFieldOptions = useMemo(() => getConditionalFieldOptions(), []);
  const [validationTypes, setValidationTypes] = useState([]);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [loadingFlow, setLoadingFlow] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [houseTypes, setHouseTypes] = useState([]);
  const [houseTypesLoading, setHouseTypesLoading] = useState(true);
  const [houseTypesError, setHouseTypesError] = useState(null);
  const [occupations, setOccupations] = useState([]);
  const [occupationsLoading, setOccupationsLoading] = useState(false);
  const [occupationsError, setOccupationsError] = useState(null);
  const occupationsFetchedRef = useRef(false);
  const [sectorOccupations, setSectorOccupations] = useState([]);
  const [sectorOccupationsLoading, setSectorOccupationsLoading] = useState(false);
  const [sectorOccupationsError, setSectorOccupationsError] = useState(null);
  const sectorOccupationsFetchedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      getWhatsappMessageTypes(),
      getWhatsappMessageParameters(),
      getWhatsappValidationTypes(),
    ]).then(([messageOptions, parameterOpts, validationOptions]) => {
      if (!isMounted) return;
      setMessageTypes(messageOptions);
      setParameterOptions(parameterOpts);
      setValidationTypes(validationOptions);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setHouseTypesLoading(true);
    setHouseTypesError(null);

    getPublicHouseTypes()
      .then((items) => {
        if (!isMounted) return;
        setHouseTypes(items);
      })
      .catch((error) => {
        if (!isMounted) return;
        setHouseTypes([]);
        setHouseTypesError(
          error?.response?.data?.message ||
            error?.message ||
            "No se pudieron cargar las opciones de tipo de vivienda."
        );
      })
      .finally(() => {
        if (isMounted) setHouseTypesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!flowUuid) {
      setFlowName("Flujo WhatsApp");
      setFlowDescription("");
      setFlowRole("PRIMARY");
      setNodes(initialNodes);
      setEdges([]);
      setSelectedNodeId(initialNodes[0].id);
      setSelectedEdgeId(null);
      setLoadingFlow(false);
      return;
    }

    let cancelled = false;
    setLoadingFlow(true);

    getStepConnectionByUuid(flowUuid)
      .then((flow) => {
        if (cancelled) return;
        setFlowName(flow.name || "Flujo WhatsApp");
        setFlowDescription(flow.description || "");
        setFlowRole(flow.flowRole || "PRIMARY");
        if (flow.nodes && flow.nodes.length > 0) {
          setNodes(flow.nodes);
          setEdges(Array.isArray(flow.edges) ? flow.edges : []);
          setSelectedNodeId(flow.nodes[0].id);
        } else {
          setNodes(initialNodes);
          setEdges([]);
          setSelectedNodeId(initialNodes[0].id);
        }
        setSelectedEdgeId(null);
      })
      .catch(() => {
        if (cancelled) return;
        showSnackbar("No se pudo cargar el flujo desde el servidor.", "error");
        setNodes(initialNodes);
        setEdges([]);
        setSelectedNodeId(initialNodes[0].id);
      })
      .finally(() => {
        if (!cancelled) setLoadingFlow(false);
      });

    return () => {
      cancelled = true;
    };
  }, [flowUuid]);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  useEffect(() => {
    if (!selectedNode || !stepUsesOccupationOptions(selectedNode)) return;
    if (occupationsFetchedRef.current) return;

    let isMounted = true;
    setOccupationsLoading(true);
    setOccupationsError(null);

    getPublicOccupations()
      .then((items) => {
        if (!isMounted) return;
        setOccupations(items);
        occupationsFetchedRef.current = true;
      })
      .catch((error) => {
        if (!isMounted) return;
        setOccupations([]);
        setOccupationsError(
          error?.response?.data?.message ||
            error?.message ||
            "No se pudieron cargar las opciones de ocupación."
        );
        occupationsFetchedRef.current = true;
      })
      .finally(() => {
        if (isMounted) setOccupationsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedNode]);

  useEffect(() => {
    if (!selectedNode || !stepUsesSectorOccupationOptions(selectedNode)) return;
    if (sectorOccupationsFetchedRef.current) return;

    let isMounted = true;
    setSectorOccupationsLoading(true);
    setSectorOccupationsError(null);

    getPublicSectorOccupations()
      .then((items) => {
        if (!isMounted) return;
        setSectorOccupations(items);
        sectorOccupationsFetchedRef.current = true;
      })
      .catch((error) => {
        if (!isMounted) return;
        setSectorOccupations([]);
        setSectorOccupationsError(
          error?.response?.data?.message ||
            error?.message ||
            "No se pudieron cargar las opciones de sector de ocupación."
        );
        sectorOccupationsFetchedRef.current = true;
      })
      .finally(() => {
        if (isMounted) setSectorOccupationsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedNode]);

  const flowPayload = useMemo(
    () =>
      prepareStepConnectionPayload(
        buildWhatsappFlowPayload({
          name: flowName,
          description: flowDescription,
          flowRole,
          nodes,
          edges,
        })
      ),
    [flowName, flowDescription, flowRole, nodes, edges]
  );

  const placeholderWarnings = useMemo(() => {
    if (!selectedNode) return [];
    const mt = selectedNode?.data?.messageType;
    if (mt !== TEMPLATE_TEXT && mt !== TEMPLATE_BUTTONS) return [];
    const msg = selectedNode?.data?.message || "";
    return getInvalidMessagePlaceholders(msg);
  }, [selectedNode]);

  /**
   * Detecta el caso "olvidaste poner la variable de lista": el paso tiene una validación
   * que mapea a una variable `*_options`, el mensaje usa la variable de respuesta
   * correspondiente (p. ej. `{{house_type}}`) pero NO la de lista (`{{house_type_options}}`).
   * El backend cargará la lista, pero al renderizar se sustituye la respuesta vacía por N/A.
   */
  const optionsMismatchWarnings = useMemo(() => {
    if (!selectedNode) return [];
    const mt = selectedNode?.data?.messageType;
    if (mt !== TEMPLATE_TEXT && mt !== TEMPLATE_BUTTONS) return [];
    const message = selectedNode?.data?.message || "";
    const rules = selectedNode?.data?.validationRules || [];
    const seenPairs = new Set();
    const warnings = [];
    for (const rule of rules) {
      const optionsVar = VALIDATION_TO_SPECIAL_VARIABLE[rule?.type];
      if (!optionsVar) continue;
      const answerVar = Object.entries(ANSWER_TO_OPTIONS_VAR).find(
        ([, optsKey]) => optsKey === optionsVar
      )?.[0];
      if (!answerVar) continue;
      const hasOptions = message.includes(`{{${optionsVar}}}`);
      const hasAnswer = message.includes(`{{${answerVar}}}`);
      const pairKey = `${answerVar}->${optionsVar}`;
      if (hasAnswer && !hasOptions && !seenPairs.has(pairKey)) {
        seenPairs.add(pairKey);
        warnings.push({ answerVar, optionsVar });
      }
    }
    return warnings;
  }, [selectedNode]);

  const missingSaveValidation = useMemo(() => {
    if (!selectedNode) return null;
    return missingRequiredValidationForSaveVariable(selectedNode.data);
  }, [selectedNode]);

  const pagareUrlWarning = useMemo(() => {
    if (!selectedNode) return false;
    return stepMissingPagareValidationForUrl(selectedNode.data);
  }, [selectedNode]);

  /**
   * Detecta variables especiales manuales usadas como `{{key}}` en el mensaje pero NO
   * activadas con su checkbox. En runtime el backend solo carga las variables declaradas
   * en `variables`; si falta el checkbox, `{{value_approve}}` se renderiza como "N/A".
   */
  const missingManualSpecialWarnings = useMemo(() => {
    if (!selectedNode) return [];
    const mt = selectedNode?.data?.messageType;
    if (mt !== TEMPLATE_TEXT && mt !== TEMPLATE_BUTTONS) return [];
    const message = selectedNode?.data?.message || "";
    const manualSelected = selectedNode?.data?.manualSpecialVariables || [];
    return MANUAL_SPECIAL_VARIABLE_KEYS.filter(
      (key) => message.includes(`{{${key}}}`) && !manualSelected.includes(key)
    );
  }, [selectedNode]);

  const showHouseTypeOptionsInfo = useMemo(
    () => Boolean(selectedNode && stepUsesHouseTypeOptions(selectedNode)),
    [selectedNode]
  );

  const houseTypeOptionsInfoMessage = useMemo(() => {
    if (houseTypesLoading) {
      return "Cargando opciones de tipo de vivienda...";
    }
    if (houseTypes.length === 0) {
      return "No hay opciones de tipo de vivienda configuradas en este momento.";
    }
    const numberedList = formatNumberedList(houseTypes);
    return `Las opciones de tipo de vivienda se mostrarán al usuario en este orden: ${numberedList}. El usuario debe responder con el número de la opción.`;
  }, [houseTypes, houseTypesLoading]);

  const showOccupationOptionsInfo = useMemo(
    () => Boolean(selectedNode && stepUsesOccupationOptions(selectedNode)),
    [selectedNode]
  );

  const occupationOptionsInfoMessage = useMemo(() => {
    if (occupationsLoading) {
      return "Cargando opciones de ocupación...";
    }
    if (occupations.length === 0) {
      return "No hay opciones de ocupación configuradas en este momento.";
    }
    const numberedList = formatNumberedList(occupations);
    return `Las opciones de ocupación se mostrarán al usuario en este orden: ${numberedList}. El usuario debe responder con el número de la opción.`;
  }, [occupations, occupationsLoading]);

  const showSectorOccupationOptionsInfo = useMemo(
    () => Boolean(selectedNode && stepUsesSectorOccupationOptions(selectedNode)),
    [selectedNode]
  );

  const sectorOccupationOptionsInfoMessage = useMemo(() => {
    if (sectorOccupationsLoading) {
      return "Cargando opciones de sector de ocupación...";
    }
    if (sectorOccupations.length === 0) {
      return "No hay opciones de sector de ocupación configuradas en este momento.";
    }
    const numberedList = formatNumberedList(sectorOccupations.map((item) => item.name));
    return `Las opciones de sector de ocupación se mostrarán al usuario en este orden: ${numberedList}. El usuario debe responder con el número de la opción.`;
  }, [sectorOccupations, sectorOccupationsLoading]);

  const genders = useLazyCatalog(
    selectedNode,
    stepUsesGenderOptions,
    getPublicGenders,
    "No se pudieron cargar las opciones de género."
  );
  const maritalStatuses = useLazyCatalog(
    selectedNode,
    stepUsesMaritalStatusOptions,
    getPublicMaritalStatuses,
    "No se pudieron cargar las opciones de estado civil."
  );

  const showGenderOptionsInfo = useMemo(
    () => Boolean(selectedNode && stepUsesGenderOptions(selectedNode)),
    [selectedNode]
  );
  const showMaritalStatusOptionsInfo = useMemo(
    () => Boolean(selectedNode && stepUsesMaritalStatusOptions(selectedNode)),
    [selectedNode]
  );

  const buildLabelOptionsInfoMessage = (catalog, name) => {
    if (catalog.loading) return `Cargando opciones de ${name}...`;
    if (catalog.items.length === 0) {
      return `No hay opciones de ${name} configuradas en este momento.`;
    }
    const numberedList = formatNumberedList(catalog.items.map((item) => item.label));
    return `Las opciones de ${name} se mostrarán al usuario en este orden: ${numberedList}. El usuario debe responder con el número de la opción.`;
  };
  const genderOptionsInfoMessage = buildLabelOptionsInfoMessage(genders, "género");
  const maritalStatusOptionsInfoMessage = buildLabelOptionsInfoMessage(
    maritalStatuses,
    "estado civil"
  );

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((current) => ({ ...current, open: false }));
  };

  const onConnect = useCallback(
    (params) =>
      setEdges((currentEdges) =>
        addEdge({ ...params, type: "smoothstep" }, currentEdges)
      ),
    [setEdges]
  );

  const updateSelectedNodeData = (field, value) => {
    if (!selectedNodeId) return;

    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        node.id === selectedNodeId
          ? { ...node, data: { ...node.data, [field]: value } }
          : node
      )
    );
  };

  const patchSelectedNodeData = (partialData) => {
    if (!selectedNodeId || !partialData || typeof partialData !== "object") return;

    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        node.id === selectedNodeId
          ? { ...node, data: { ...node.data, ...partialData } }
          : node
      )
    );
  };

  const handleStepUserChange = (nextUser) => {
    if (!selectedNodeId) return;

    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        if (node.id !== selectedNodeId) return node;

        const keepsReleaseEnabled =
          node.data?.user === "COMMERCE" &&
          nextUser === "COMMERCE" &&
          node.data?.releaseCommerceAfterResponse === true;

        return {
          ...node,
          data: {
            ...node.data,
            user: nextUser,
            releaseCommerceAfterResponse: keepsReleaseEnabled,
          },
        };
      })
    );
  };

  const setValidationRulesAndSync = useCallback(
    (nextRules) => {
      if (!selectedNodeId) return;
      const hasVideollamada = stepHasVideollamadaValidation(nextRules);
      setNodes((currentNodes) => {
        const mapped = currentNodes.map((node) => {
          if (node.id !== selectedNodeId) return node;
          const patch = { validationRules: nextRules };
          if (hasVideollamada) {
            patch.autoCompleteOnEnter = true;
            patch.autoCancelOnEnter = false;
          }
          return { ...node, data: { ...node.data, ...patch } };
        });
        const parent = mapped.find((n) => n.id === selectedNodeId);
        if (!parent) return mapped;
        const { nodes: syncedNodes, edges: syncedEdges } = syncOptionBranches(
          parent,
          getOptionsTokensFromRules(nextRules),
          mapped,
          edgesRef.current,
          createWhatsappFlowNode
        );
        setEdges(syncedEdges);
        return syncedNodes;
      });
    },
    [selectedNodeId, setNodes, setEdges]
  );

  const appendTokenToMessageEnd = (currentMessage, token) => {
    let result = currentMessage || "";
    if (result.includes(token)) return result;
    if (!result) return token;
    const endsWithSpace = /\s$/.test(result);
    return endsWithSpace ? `${result}${token}` : `${result} ${token}`;
  };

  const insertMessageParameter = (token) => {
    if (!selectedNodeId || !token) return;

    const mt = selectedNode?.data?.messageType;
    const messageVisible = mt !== TEMPLATE_WHATSAPP && mt !== TEMPLATE_DYNAMIC;
    const prevKeys = selectedNode?.data?.parameterKeys || [];

    if (!messageVisible) {
      if (!prevKeys.includes(token)) {
        patchSelectedNodeData({ parameterKeys: [...prevKeys, token] });
      }
      return;
    }

    const nextMessage = appendTokenToMessageEnd(selectedNode?.data?.message, token);
    const nextKeys = prevKeys.includes(token) ? prevKeys : [...prevKeys, token];

    patchSelectedNodeData({
      message: nextMessage,
      parameterKeys: nextKeys,
    });
  };

  const mergeParameterKeysFromMessage = (messageText, previousKeys) => {
    const tokensInMessage = extractPlaceholdersFromMessage(messageText);
    const prev = previousKeys || [];
    const merged = [...tokensInMessage];
    for (const key of prev) {
      if (!merged.includes(key)) merged.push(key);
    }
    return merged;
  };

  const handleMessageFieldChange = (event) => {
    const nextMessage = event.target.value;
    const mt = selectedNode?.data?.messageType;
    if (mt === TEMPLATE_TEXT || mt === TEMPLATE_BUTTONS) {
      const nextKeys = mergeParameterKeysFromMessage(
        nextMessage,
        selectedNode?.data?.parameterKeys
      );
      patchSelectedNodeData({ message: nextMessage, parameterKeys: nextKeys });
      return;
    }
    updateSelectedNodeData("message", nextMessage);
  };

  const handleMessageTypeChange = (nextType) => {
    const cleared = {
      message: "",
      buttons: [],
      templateName: "",
      parameterKeys: [],
    };

    if (nextType === TEMPLATE_TEXT) {
      patchSelectedNodeData({
        messageType: nextType,
        templateName: "",
        buttons: [],
      });
      return;
    }

    if (nextType === TEMPLATE_BUTTONS) {
      patchSelectedNodeData({
        messageType: nextType,
        templateName: "",
      });
      return;
    }

    if (nextType === TEMPLATE_WHATSAPP) {
      patchSelectedNodeData({
        messageType: nextType,
        message: "",
        parameterKeys: [],
      });
      return;
    }

    if (nextType === TEMPLATE_DYNAMIC) {
      patchSelectedNodeData({
        messageType: nextType,
        ...cleared,
      });
      return;
    }

    if (nextType === TEMPLATE_FLOW) {
      patchSelectedNodeData({
        messageType: nextType,
        buttons: [],
        parameterKeys: [],
      });
      return;
    }

    patchSelectedNodeData({ messageType: nextType });
  };

  const addStep = () => {
    const nextNumber = nodes.length + 1;
    const node = createWhatsappFlowNode(`step-${Date.now()}`, nextNumber, {
      x: 120 + nodes.length * 70,
      y: 120 + nodes.length * 45,
    });

    setNodes((currentNodes) => [...currentNodes, node]);
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
  };

  const deleteSelection = () => {
    if (selectedNodeId) {
      setNodes((currentNodes) =>
        currentNodes.filter((node) => node.id !== selectedNodeId)
      );
      setEdges((currentEdges) =>
        currentEdges.filter(
          (edge) =>
            edge.source !== selectedNodeId && edge.target !== selectedNodeId
        )
      );
      setSelectedNodeId(null);
      showSnackbar("Paso eliminado.", "success");
      return;
    }

    if (selectedEdgeId) {
      setEdges((currentEdges) =>
        currentEdges.filter((edge) => edge.id !== selectedEdgeId)
      );
      setSelectedEdgeId(null);
      showSnackbar("Conexión eliminada.", "success");
    }
  };

  const handleSave = async () => {
    const fraudValidationError = getFlowFraudValidationError(nodes);
    if (fraudValidationError) {
      showSnackbar(fraudValidationError, "error");
      return;
    }

    const ageRangeValidationError = getFlowAgeRangeValidationError(nodes);
    if (ageRangeValidationError) {
      showSnackbar(ageRangeValidationError, "error");
      return;
    }

    const conditionalValidation = validateAllFlowConditionals(nodes);
    if (!conditionalValidation.ok) {
      showSnackbar(conditionalValidation.error, "error");
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const { uuid } = await createStepConnection(flowPayload);
        showSnackbar("Flujo creado.", "success");
        navigate(`/homeAdmin/flujos-whatsapp/${uuid}`);
      } else {
        await updateStepConnection(flowUuid, flowPayload);
        showSnackbar("Flujo guardado.", "success");
      }
    } catch (e) {
      showSnackbar(
        e?.response?.data?.message ||
          e?.message ||
          (isNew ? "Error al crear el flujo." : "Error al guardar el flujo."),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReloadFromServer = async () => {
    if (isNew || !flowUuid) return;
    setLoadingFlow(true);
    try {
      const flow = await getStepConnectionByUuid(flowUuid);
      setFlowName(flow.name || "Flujo WhatsApp");
      setFlowDescription(flow.description || "");
      setFlowRole(flow.flowRole || "PRIMARY");
      if (flow.nodes && flow.nodes.length > 0) {
        setNodes(flow.nodes.map((n) => normalizeWhatsappFlowNode(n)));
        setEdges(Array.isArray(flow.edges) ? flow.edges : []);
        setSelectedNodeId(flow.nodes[0].id);
      }
      showSnackbar("Flujo recargado.", "success");
    } catch (e) {
      showSnackbar(
        e?.response?.data?.message ||
          e?.message ||
          "No se pudo recargar el flujo.",
        "error"
      );
    } finally {
      setLoadingFlow(false);
    }
  };

  const handleSelectionChange = ({ nodes: selectedNodes, edges: selectedEdges }) => {
    const nodeId = selectedNodes[0]?.id || null;
    const edgeId = selectedEdges[0]?.id || null;
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(edgeId);
  };

  const messageType = selectedNode?.data?.messageType;
  const showMessageField =
    selectedNode && messageType !== TEMPLATE_WHATSAPP && messageType !== TEMPLATE_DYNAMIC;
  const showParameterPicker =
    selectedNode &&
    (messageType === TEMPLATE_TEXT ||
      messageType === TEMPLATE_BUTTONS ||
      messageType === TEMPLATE_DYNAMIC);
  const showTemplateNameField =
    selectedNode && (messageType === TEMPLATE_WHATSAPP || messageType === TEMPLATE_FLOW);
  const showButtonsEditor =
    selectedNode &&
    (messageType === TEMPLATE_BUTTONS || messageType === TEMPLATE_WHATSAPP);
  const showWaFlowIdField = selectedNode && messageType === TEMPLATE_FLOW;
  const showTemplateRequiredButton = selectedNode && messageType === TEMPLATE_WHATSAPP;
  const showStepsEditionEditor = selectedNode && messageType === TEMPLATE_TEXT;

  const addButtonRow = () => {
    if (!selectedNodeId) return;
    const current = selectedNode?.data?.buttons || [];
    if (current.length >= MAX_WHATSAPP_REPLY_BUTTONS) {
      showSnackbar(`Solo se permiten hasta ${MAX_WHATSAPP_REPLY_BUTTONS} botones.`, "warning");
      return;
    }
    patchSelectedNodeData({ buttons: [...current, ""] });
  };

  const updateButtonRow = (index, value) => {
    if (!selectedNodeId) return;
    const current = [...(selectedNode?.data?.buttons || [])];
    if (index < 0 || index >= current.length) return;
    current[index] = value;
    patchSelectedNodeData({ buttons: current });
  };

  const removeButtonRow = (index) => {
    if (!selectedNodeId) return;
    const current = [...(selectedNode?.data?.buttons || [])];
    if (index < 0 || index >= current.length) return;
    current.splice(index, 1);
    patchSelectedNodeData({ buttons: current });
  };

  const validationRuleRows = selectedNode?.data?.validationRules || [];

  const addValidationRule = () => {
    const firstType = validationTypes[0]?.value || "REQUIRED";
    const meta = validationTypeMeta[firstType];
    const needsValue =
      meta &&
      meta.valueKind !== VALIDATION_VALUE_KIND.NONE &&
      meta.valueKind !== "none";
    setValidationRulesAndSync([
      ...validationRuleRows,
      { type: firstType, value: needsValue ? "" : undefined },
    ]);
  };

  const removeValidationRuleAt = (index) => {
    setValidationRulesAndSync(validationRuleRows.filter((_, i) => i !== index));
  };

  const updateValidationRuleAt = (index, partial) => {
    setValidationRulesAndSync(
      validationRuleRows.map((r, i) => (i === index ? { ...r, ...partial } : r))
    );
  };

  const updateFraudThresholdAt = (index, threshold, value) => {
    const { amountThreshold, fpdThreshold } = splitFraudValidationValue(
      validationRuleRows[index]?.value
    );
    const updatedValue =
      threshold === "amount"
        ? `${value}|${fpdThreshold}`
        : `${amountThreshold}|${value}`;
    updateValidationRuleAt(index, { value: updatedValue });
  };

  const changeValidationRuleType = (index, newType) => {
    const meta = validationTypeMeta[newType];
    const needsValue =
      meta &&
      meta.valueKind !== VALIDATION_VALUE_KIND.NONE &&
      meta.valueKind !== "none";
    setValidationRulesAndSync(
      validationRuleRows.map((r, i) => {
        if (i !== index) return r;
        if (!needsValue) return { type: newType, value: undefined };
        if (newType === "FRAUD_VALIDATION") {
          return { type: newType, value: "|" };
        }
        if (newType === "EXPERIAN_MI_DECISOR_VIABILITY") {
          return { type: newType, value: "prefilter=false" };
        }
        return { type: newType, value: r.value ?? "" };
      })
    );
  };

  if (loadingFlow) {
    return (
      <React.Fragment>
        <CssBaseline />
        <Toolbar />
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            margin: "18px 14px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress sx={{ color: "#161e55" }} />
        </Box>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <CssBaseline />
      <Toolbar />
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          margin: "18px 14px",
        }}
      >
        <Stack spacing={2}>
          <Item>.</Item>
          <Item></Item>

          <Grid container alignItems="flex-start">
            <Grid item xs={12}>
              <Box mt={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  spacing={1.5}
                  useFlexGap
                  sx={{ columnGap: 1.5, rowGap: 1 }}
                >
                  <Typography
                    component="h1"
                    variant="h5"
                    sx={{
                      m: 0,
                      color: "#161e55",
                      fontWeight: 700,
                      lineHeight: 1.3,
                      mr: { xs: 0, sm: 1 },
                    }}
                  >
                    {isNew ? "Nuevo flujo WhatsApp" : "Editar flujo WhatsApp"}
                  </Typography>
                  <ButtonPrincipal
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {isNew ? "Crear flujo" : "Guardar flujo"}
                  </ButtonPrincipal>
                  <ButtonSecondary
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/homeAdmin/flujos-whatsapp")}
                  >
                    Volver
                  </ButtonSecondary>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Crea pasos, configura mensajes y conecta el recorrido visualmente.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, borderRadius: "18px", width: "100%", boxSizing: "border-box" }}>
            <Stack spacing={2}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Nombre del flujo"
                  value={flowName}
                  onChange={(event) => setFlowName(event.target.value)}
                />
                <TextField
                  fullWidth
                  label="Descripción del flujo"
                  value={flowDescription}
                  onChange={(event) => setFlowDescription(event.target.value)}
                  multiline
                  minRows={2}
                  placeholder="Opcional. Visible en la lista de flujos."
                />
                <FormControl fullWidth size="small">
                  <InputLabel id="flow-role-label">Tipo de flujo</InputLabel>
                  <Select
                    labelId="flow-role-label"
                    label="Tipo de flujo"
                    value={flowRole}
                    onChange={(event) => setFlowRole(event.target.value)}
                  >
                    {FLOW_ROLE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <ButtonPrincipal variant="contained" onClick={addStep}>
                  Agregar paso
                </ButtonPrincipal>
                <ButtonSecondary variant="outlined" onClick={deleteSelection}>
                  Eliminar selección
                </ButtonSecondary>
                {!isNew ? (
                  <ButtonSecondary variant="outlined" onClick={handleReloadFromServer}>
                    Recargar desde servidor
                  </ButtonSecondary>
                ) : null}
                <ButtonSecondary variant="outlined" onClick={() => setJsonDialogOpen(true)}>
                  Ver JSON
                </ButtonSecondary>
              </Stack>
            </Stack>
          </Paper>

          {houseTypesError ? (
            <Alert severity="error">
              No se pudieron cargar las opciones de tipo de vivienda. Recarga la página o
              inténtalo más tarde.
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: "stretch",
              gap: 2,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, minHeight: { xs: "50vh", lg: "auto" } }}>
              <Paper
                sx={{
                  height: "68vh",
                  borderRadius: "18px",
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  nodeTypes={nodeTypes}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onSelectionChange={handleSelectionChange}
                  fitView
                >
                  <Background />
                  <Controls />
                  <MiniMap pannable zoomable />
                </ReactFlow>
              </Paper>
            </Box>

            <Box
              sx={{
                width: { xs: "100%", lg: 420 },
                maxWidth: "100%",
                flexShrink: 0,
              }}
            >
            <Panel>
              {selectedNode ? (
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ color: "#161e55", fontWeight: 700 }}>
                    Configuración del paso
                  </Typography>
                  <TextField
                    label="Nombre"
                    value={selectedNode.data?.name || ""}
                    onChange={(event) =>
                      updateSelectedNodeData("name", event.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    label="Descripción del paso"
                    value={selectedNode.data?.description || ""}
                    onChange={(event) =>
                      updateSelectedNodeData("description", event.target.value)
                    }
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Opcional."
                  />
                  <FormControl fullWidth>
                    <InputLabel id="user-select-label">Usuario</InputLabel>
                    <Select
                      labelId="user-select-label"
                      label="Usuario"
                      value={selectedNode.data?.user || "COMMERCE"}
                      onChange={(event) => handleStepUserChange(event.target.value)}
                    >
                      {userOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          selectedNode.data?.user === "COMMERCE" &&
                          selectedNode.data?.releaseCommerceAfterResponse === true
                        }
                        disabled={selectedNode.data?.user !== "COMMERCE"}
                        onChange={(event) =>
                          updateSelectedNodeData(
                            "releaseCommerceAfterResponse",
                            event.target.checked
                          )
                        }
                      />
                    }
                    label="Liberar al comercio"
                  />
                  <FormControl fullWidth>
                    <InputLabel id="language-select-label">Idioma de plantilla</InputLabel>
                    <Select
                      labelId="language-select-label"
                      label="Idioma de plantilla"
                      value={selectedNode.data?.templateLanguage || "es"}
                      onChange={(event) =>
                        updateSelectedNodeData("templateLanguage", event.target.value)
                      }
                    >
                      {templateLanguageOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="message-type-label">Tipo de mensaje</InputLabel>
                    <Select
                      labelId="message-type-label"
                      label="Tipo de mensaje"
                      value={selectedNode.data?.messageType || ""}
                      onChange={(event) => handleMessageTypeChange(event.target.value)}
                    >
                      {messageTypes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {showTemplateNameField ? (
                    <TextField
                      label="Nombre de la plantilla"
                      value={selectedNode.data?.templateName || ""}
                      onChange={(event) =>
                        updateSelectedNodeData("templateName", event.target.value)
                      }
                      fullWidth
                    />
                  ) : null}
                  {showTemplateRequiredButton ? (
                    <TextField
                      label="Botón requerido de la plantilla"
                      value={selectedNode.data?.templateRequiredButtonId || ""}
                      onChange={(event) =>
                        updateSelectedNodeData("templateRequiredButtonId", event.target.value)
                      }
                      fullWidth
                      placeholder="Label del botón que se debe responder"
                    />
                  ) : null}
                  {showWaFlowIdField ? (
                    <TextField
                      label="ID del flujo WhatsApp"
                      value={selectedNode.data?.waFlowId || ""}
                      onChange={(event) =>
                        updateSelectedNodeData("waFlowId", event.target.value)
                      }
                      fullWidth
                      placeholder="ID del flujo de WhatsApp"
                    />
                  ) : null}
                  {showMessageField ? (
                    <>
                      {placeholderWarnings.length > 0 ? (
                        <Alert severity="warning">
                          Hay placeholders en el mensaje que no son válidos o no están en
                          el catálogo permitido: {placeholderWarnings.join(", ")}
                        </Alert>
                      ) : null}
                      <TextField
                        label="Mensaje"
                        value={selectedNode.data?.message || ""}
                        onChange={handleMessageFieldChange}
                        fullWidth
                        multiline
                        minRows={4}
                        error={placeholderWarnings.length > 0}
                        helperText={
                          placeholderWarnings.length > 0
                            ? "Usa solo tokens del catálogo (formato {{token}}) o insértalos desde la lista."
                            : "Escribe el mensaje o usa la lista de abajo para insertar {{tokens}} al final del texto."
                        }
                      />
                      {optionsMismatchWarnings.length > 0 ? (
                        <Alert severity="warning">
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Posible error de configuración del paso:
                          </Typography>
                          {optionsMismatchWarnings.map(({ answerVar, optionsVar }) => (
                            <Typography
                              key={answerVar}
                              variant="body2"
                              component="div"
                              sx={{ mb: 0.25 }}
                            >
                              El mensaje usa <code>{`{{${answerVar}}}`}</code> (la variable
                              donde se guarda la respuesta del cliente), pero la validación
                              del paso requiere mostrar la lista cargada desde la API.
                              Probablemente quieras usar <code>{`{{${optionsVar}}}`}</code>{" "}
                              para que se rendericen las opciones; de lo contrario el
                              mensaje saldrá con "N/A".
                            </Typography>
                          ))}
                        </Alert>
                      ) : null}
                      {missingManualSpecialWarnings.length > 0 ? (
                        <Alert severity="warning">
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Variable especial sin activar:
                          </Typography>
                          {missingManualSpecialWarnings.map((key) => (
                            <Typography
                              key={key}
                              variant="body2"
                              component="div"
                              sx={{ mb: 0.25 }}
                            >
                              El mensaje usa <code>{`{{${key}}}`}</code> pero la variable
                              especial «{SPECIAL_VARIABLE_META[key]?.label || key}» no está
                              activada en este paso. Actívala en «Variables especiales» (abajo)
                              para que el backend cargue su valor; de lo contrario el mensaje
                              saldrá con "N/A".
                            </Typography>
                          ))}
                        </Alert>
                      ) : null}
                    </>
                  ) : null}
                  <TextField
                    label="Mensaje de error"
                    value={selectedNode.data?.templateTextError || ""}
                    onChange={(event) =>
                      updateSelectedNodeData("templateTextError", event.target.value)
                    }
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Opcional. Mensaje que se muestra si la validación falla."
                  />
                  {showParameterPicker && parameterOptions.length === 0 ? (
                    <Alert severity="info">
                      Aún no hay parámetros configurados en el catálogo temporal. Cuando el
                      backend exponga la lista, aparecerán aquí para insertarlos en el mensaje.
                    </Alert>
                  ) : null}
                  {showParameterPicker ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: "#161e55", mb: 1 }}>
                        Parámetros (insertar)
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ maxHeight: 220, overflow: "auto", borderRadius: "12px" }}
                      >
                        <List dense disablePadding>
                          {parameterOptions.map((parameter) => (
                            <ListItemButton
                              key={parameter.value}
                              onClick={() => insertMessageParameter(parameter.value)}
                            >
                              <ListItemText
                                primary={parameter.label}
                                secondary={
                                  parameter.description
                                    ? `${parameter.value} — ${parameter.description}`
                                    : parameter.value
                                }
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Paper>
                      <FormHelperText sx={{ mt: 1 }}>
                        {messageType === TEMPLATE_DYNAMIC
                          ? "Toca un parámetro para marcarlo en este paso (no se muestra como lista de seleccionados)."
                          : "Toca un parámetro para insertar su token al final del mensaje si falta."}
                      </FormHelperText>
                    </Box>
                  ) : null}
                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="subtitle2" sx={{ color: "#161e55" }}>
                        Validaciones del paso
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={addValidationRule}
                        aria-label="Añadir validación"
                        disabled={validationTypes.length === 0}
                      >
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </Stack>
                    {validationRuleRows.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Sin reglas. Usa + para añadir una. El payload las serializa en un solo
                        campo separado por comas.
                      </Typography>
                    ) : null}
                    <Stack spacing={1.5}>
                      {validationRuleRows.map((rule, index) => {
                        const meta = validationTypeMeta[rule.type];
                        const kind = meta?.valueKind ?? VALIDATION_VALUE_KIND.NONE;
                        const showValue =
                          kind !== VALIDATION_VALUE_KIND.NONE && kind !== "none";
                        const fraudThresholds =
                          rule.type === "FRAUD_VALIDATION"
                            ? splitFraudValidationValue(rule.value)
                            : null;
                        const hasBackgroundStart = validationRuleRows.some(
                          (item) => item.type === "BACKGROUND_CHECK_START"
                        );
                        const hasBackgroundResult = validationRuleRows.some(
                          (item) => item.type === "BACKGROUND_CHECK_RESULT"
                        );
                        // Solo una regla de fraude por paso: el agente evalúa una sola.
                        const fraudInOtherRule = validationRuleRows.some(
                          (item, i) => i !== index && item.type === "FRAUD_VALIDATION"
                        );

                        return (
                          <Paper
                            key={`${selectedNode.id}-rule-${index}`}
                            variant="outlined"
                            sx={{ p: 1.5, borderRadius: "12px" }}
                          >
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <FormControl fullWidth size="small">
                                  <InputLabel id={`rule-type-${index}`}>Tipo</InputLabel>
                                  <Select
                                    labelId={`rule-type-${index}`}
                                    label="Tipo"
                                    value={rule.type || ""}
                                    onChange={(event) =>
                                      changeValidationRuleType(index, event.target.value)
                                    }
                                  >
                                    {validationTypes.map((opt) => (
                                      <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                        disabled={opt.value === "FRAUD_VALIDATION" && fraudInOtherRule}
                                      >
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <IconButton
                                  size="small"
                                  onClick={() => removeValidationRuleAt(index)}
                                  aria-label={`Quitar validación ${index + 1}`}
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              </Stack>
                              {rule.type === "FRAUD_VALIDATION" ? (
                                <>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Umbral de monto (COP)"
                                    value={fraudThresholds.amountThreshold}
                                    onChange={(event) =>
                                      updateFraudThresholdAt(index, "amount", event.target.value)
                                    }
                                    inputProps={{ min: 1, step: 1, inputMode: "numeric" }}
                                    helperText="Ingresa un monto entero mayor que cero."
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Umbral FPD"
                                    value={fraudThresholds.fpdThreshold}
                                    onChange={(event) =>
                                      updateFraudThresholdAt(index, "fpd", event.target.value)
                                    }
                                    inputProps={{ min: 0, max: 1, step: "any", inputMode: "decimal" }}
                                    helperText="Usa un valor entre 0 y 1, por ejemplo 0.65."
                                  />
                                  <Alert severity="info">
                                    Ubica esta validación después del paso que crea el crédito.
                                  </Alert>
                                </>
                              ) : null}
                              {rule.type === "AGE_RANGE" ? (
                                <Alert severity="info">
                                  Usa el año de nacimiento que un paso anterior guardó en el campo
                                  birth. Si la edad queda fuera del rango (ambos extremos incluidos),
                                  la solicitud se rechaza y se cierra con el mensaje de error de este
                                  paso: no menciones la edad en ese mensaje. Si no hay año de
                                  nacimiento, también se rechaza y se avisa al admin.
                                </Alert>
                              ) : null}
                              {rule.type === "BACKGROUND_CHECK_START" ? (
                                <Alert severity="info">
                                  Esta regla inicia la consulta de antecedentes TusDatos.
                                </Alert>
                              ) : null}
                              {rule.type === "BACKGROUND_CHECK_RESULT" ? (
                                <Alert severity="info">
                                  Esta regla revisa el resultado de TusDatos. Se recomienda ubicarla
                                  en un paso posterior al inicio.
                                </Alert>
                              ) : null}
                              {index === 0 && hasBackgroundStart && hasBackgroundResult ? (
                                <Alert severity="warning">
                                  Se recomienda separar el inicio y la revisión de antecedentes en
                                  pasos distintos.
                                </Alert>
                              ) : null}
                              {showValue && kind === VALIDATION_VALUE_KIND.NUMBER ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Valor"
                                  value={rule.value ?? ""}
                                  onChange={(event) =>
                                    updateValidationRuleAt(index, {
                                      value: event.target.value,
                                    })
                                  }
                                  inputProps={{ step: 1 }}
                                />
                              ) : null}
                              {showValue && kind === VALIDATION_VALUE_KIND.OPTIONS ? (
                                <Box>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mb: 0.5 }}
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      Opciones (una por campo; en el flujo se envían unidas
                                      automáticamente)
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const rows = [...optionsRowsFromRuleValue(rule.value)];
                                        rows.push("");
                                        updateValidationRuleAt(index, {
                                          value: optionsRuleValueFromRows(rows),
                                        });
                                      }}
                                      aria-label="Agregar opción"
                                    >
                                      <AddCircleOutlineIcon />
                                    </IconButton>
                                  </Stack>
                                  <Stack spacing={1}>
                                    {optionsRowsFromRuleValue(rule.value).map((cell, optIdx) => (
                                      <Stack
                                        key={`opt-${index}-${optIdx}`}
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label={`Opción ${optIdx + 1}`}
                                          placeholder="Texto de la opción"
                                          value={cell}
                                          onChange={(event) => {
                                            const rows = [
                                              ...optionsRowsFromRuleValue(rule.value),
                                            ];
                                            rows[optIdx] = event.target.value;
                                            updateValidationRuleAt(index, {
                                              value: optionsRuleValueFromRows(rows),
                                            });
                                          }}
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const rows = optionsRowsFromRuleValue(rule.value);
                                            const next = rows.filter((_, j) => j !== optIdx);
                                            if (next.length === 0) next.push("");
                                            updateValidationRuleAt(index, {
                                              value: optionsRuleValueFromRows(next),
                                            });
                                          }}
                                          aria-label={`Quitar opción ${optIdx + 1}`}
                                        >
                                          <RemoveCircleOutlineIcon />
                                        </IconButton>
                                      </Stack>
                                    ))}
                                  </Stack>
                                </Box>
                              ) : null}
                              {showValue && kind === VALIDATION_VALUE_KIND.REGEX ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Patrón (regex)"
                                  multiline
                                  minRows={2}
                                  value={rule.value ?? ""}
                                  onChange={(event) =>
                                    updateValidationRuleAt(index, {
                                      value: event.target.value,
                                    })
                                  }
                                />
                              ) : null}
                              {showValue &&
                              kind === VALIDATION_VALUE_KIND.KEY_VALUE &&
                              meta?.prefilterToggle ? (
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isExperianDecisorPrefilterYes(rule.value)}
                                      onChange={(event) =>
                                        updateValidationRuleAt(index, {
                                          value: event.target.checked
                                            ? "prefilter=true"
                                            : "prefilter=false",
                                        })
                                      }
                                      color="primary"
                                    />
                                  }
                                  label="¿Es pre filtro?"
                                />
                              ) : null}
                              {showValue &&
                              kind !== VALIDATION_VALUE_KIND.NUMBER &&
                              kind !== VALIDATION_VALUE_KIND.OPTIONS &&
                              kind !== VALIDATION_VALUE_KIND.REGEX &&
                              kind !== VALIDATION_VALUE_KIND.FRAUD_THRESHOLDS &&
                              !(kind === VALIDATION_VALUE_KIND.KEY_VALUE && meta?.prefilterToggle) ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Valor"
                                  placeholder={meta?.placeholder || ""}
                                  value={rule.value ?? ""}
                                  onChange={(event) =>
                                    updateValidationRuleAt(index, {
                                      value: event.target.value,
                                    })
                                  }
                                  helperText={
                                    rule.type === "AGE_RANGE"
                                      ? getAgeRangeValidationError(rule) ||
                                        "Edad mínima-máxima en años, por ejemplo 18-69."
                                      : kind === VALIDATION_VALUE_KIND.KEY_VALUE
                                        ? "Se envía como TIPO:valor en la cadena serializada."
                                        : undefined
                                  }
                                  error={rule.type === "AGE_RANGE" && Boolean(getAgeRangeValidationError(rule))}
                                />
                              ) : null}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>

                  {showHouseTypeOptionsInfo && !houseTypesError ? (
                    <Alert severity="info">
                      <Typography variant="body2">{houseTypeOptionsInfoMessage}</Typography>
                    </Alert>
                  ) : null}

                  {showGenderOptionsInfo && genders.error ? (
                    <Alert severity="error">
                      <Typography variant="body2">{genders.error}</Typography>
                    </Alert>
                  ) : null}

                  {showGenderOptionsInfo && !genders.error ? (
                    <Alert severity="info">
                      <Typography variant="body2">{genderOptionsInfoMessage}</Typography>
                    </Alert>
                  ) : null}

                  {showMaritalStatusOptionsInfo && maritalStatuses.error ? (
                    <Alert severity="error">
                      <Typography variant="body2">{maritalStatuses.error}</Typography>
                    </Alert>
                  ) : null}

                  {showMaritalStatusOptionsInfo && !maritalStatuses.error ? (
                    <Alert severity="info">
                      <Typography variant="body2">{maritalStatusOptionsInfoMessage}</Typography>
                    </Alert>
                  ) : null}

                  {showOccupationOptionsInfo && occupationsError ? (
                    <Alert severity="error">
                      <Typography variant="body2">{occupationsError}</Typography>
                    </Alert>
                  ) : null}

                  {showOccupationOptionsInfo && !occupationsError ? (
                    <Alert severity="info">
                      <Typography variant="body2">{occupationOptionsInfoMessage}</Typography>
                    </Alert>
                  ) : null}

                  {showSectorOccupationOptionsInfo && sectorOccupationsError ? (
                    <Alert severity="error">
                      <Typography variant="body2">{sectorOccupationsError}</Typography>
                    </Alert>
                  ) : null}

                  {showSectorOccupationOptionsInfo && !sectorOccupationsError ? (
                    <Alert severity="info">
                      <Typography variant="body2">{sectorOccupationOptionsInfoMessage}</Typography>
                    </Alert>
                  ) : null}

                  {/* Variables especiales del paso (campo `variables` del payload) */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: "#161e55", mb: 1 }}>
                      Variables especiales
                    </Typography>
                    {(() => {
                      const rules = selectedNode.data?.validationRules || [];
                      const autoSpecials = Array.from(
                        new Set([
                          ...rules
                            .map((r) => VALIDATION_TO_SPECIAL_VARIABLE[r?.type])
                            .filter(Boolean),
                          ...(stepUsesQuotaOptionsPlaceholder(selectedNode.data)
                            ? [QUOTA_OPTIONS_VARIABLE]
                            : []),
                          ...(stepUsesCurrentBalancePlaceholder(selectedNode.data)
                            ? [CURRENT_BALANCE_VARIABLE]
                            : []),
                          ...(stepUsesMaxApprovedExtensionPlaceholder(selectedNode.data)
                            ? [MAX_APPROVED_EXTENSION_VARIABLE]
                            : []),
                          ...(stepUsesExtensionQuotaOptionsPlaceholder(selectedNode.data)
                            ? [EXTENSION_QUOTA_OPTIONS_VARIABLE]
                            : []),
                        ])
                      );
                      const manualSelected = selectedNode.data?.manualSpecialVariables || [];

                      const toggleManual = (key, checked) => {
                        const next = checked
                          ? Array.from(new Set([...manualSelected, key]))
                          : manualSelected.filter((k) => k !== key);
                        patchSelectedNodeData({ manualSpecialVariables: next });
                      };

                      return (
                        <Stack spacing={1.5}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Automáticas (según validación):
                            </Typography>
                            {autoSpecials.length > 0 ? (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {autoSpecials.map((key) => (
                                  <Chip
                                    key={key}
                                    size="small"
                                    label={SPECIAL_VARIABLE_META[key]?.label || key}
                                    title={SPECIAL_VARIABLE_META[key]?.description || key}
                                    sx={{ backgroundColor: "#e8f5e9" }}
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Ninguna. Se derivarán automáticamente cuando agregues una
                                validación que las requiera (p. ej. HOUSE_TYPE_INDEX_ALLOWED).
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Manuales:
                            </Typography>
                            <Stack>
                              {MANUAL_SPECIAL_VARIABLE_KEYS.map((key) => {
                                const meta = SPECIAL_VARIABLE_META[key];
                                const checked = manualSelected.includes(key);
                                return (
                                  <FormControlLabel
                                    key={key}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={(event) =>
                                          toggleManual(key, event.target.checked)
                                        }
                                      />
                                    }
                                    label={
                                      <Box>
                                        <Typography variant="body2">
                                          {meta?.label || key}
                                        </Typography>
                                        {meta?.description ? (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {meta.description}
                                          </Typography>
                                        ) : null}
                                      </Box>
                                    }
                                    sx={{ ml: 0, alignItems: "flex-start" }}
                                  />
                                );
                              })}
                            </Stack>
                          </Box>
                          <FormHelperText>
                            Estas variables se envían en el campo <code>variables</code> del
                            payload. Las automáticas dependen de la validación; las manuales
                            se activan con los checkboxes.
                          </FormHelperText>
                        </Stack>
                      );
                    })()}
                  </Box>

                  {showButtonsEditor ? (
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="subtitle2" sx={{ color: "#161e55" }}>
                          Botones (máx. {MAX_WHATSAPP_REPLY_BUTTONS})
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={addButtonRow}
                          aria-label="Agregar botón"
                          disabled={
                            (selectedNode.data?.buttons || []).length >=
                            MAX_WHATSAPP_REPLY_BUTTONS
                          }
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Stack>
                      {(selectedNode.data?.buttons || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Aún no hay botones. Usa el botón + para agregar uno.
                        </Typography>
                      ) : null}
                      {(selectedNode.data?.buttons || []).map((label, index) => (
                        <Stack
                          key={`btn-row-${index}`}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <TextField
                            label={`Botón ${index + 1}`}
                            value={label}
                            onChange={(event) => updateButtonRow(index, event.target.value)}
                            fullWidth
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeButtonRow(index)}
                            aria-label={`Eliminar botón ${index + 1}`}
                          >
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  ) : null}

                  <TextField
                    label="Mensaje de transición"
                    value={selectedNode.data?.transitionMessage || ""}
                    onChange={(event) =>
                      updateSelectedNodeData("transitionMessage", event.target.value)
                    }
                    fullWidth
                    placeholder="Mensaje al cambiar de usuario entre pasos."
                  />

                  <Divider />

                  <WhatsappFlowNotificationsSection
                    data={selectedNode.data}
                    onPatch={patchSelectedNodeData}
                    parameterOptions={parameterOptions}
                  />

                  <Divider />

                  {missingSaveValidation ? (
                    <Alert severity="warning">
                      <Typography variant="body2">
                        Este paso guarda{" "}
                        {selectedNode.data.saveVariable === "gender" ? "Género" : "Estado civil"}:
                        agrega la validación «
                        {validationTypeMeta[missingSaveValidation]?.label || missingSaveValidation}
                        » y usa{" "}
                        <code>
                          {`{{${VALIDATION_TO_SPECIAL_VARIABLE[missingSaveValidation]}}}`}
                        </code>{" "}
                        en el mensaje.
                      </Typography>
                    </Alert>
                  ) : null}

                  {pagareUrlWarning ? (
                    <Alert severity="warning">
                      <Typography variant="body2">
                        El mensaje usa <code>{PAGARE_URL_TOKEN}</code> pero el paso no tiene
                        la validación «Pagaré». Agrega la validación PAGARE para que el
                        backend genere la URL.
                      </Typography>
                    </Alert>
                  ) : null}

                  <WhatsappFlowConditionalMessagesSection
                    data={selectedNode.data}
                    onPatch={patchSelectedNodeData}
                    fieldOptions={conditionalFieldOptions}
                    parameterOptions={parameterOptions}
                  />

                  {showStepsEditionEditor ? (
                    <>
                      <Divider />
                      <WhatsappFlowStepsEditionSection
                        data={selectedNode.data}
                        onPatch={patchSelectedNodeData}
                        nodes={nodes}
                        currentNodeId={selectedNode.id}
                      />
                    </>
                  ) : null}

                  <Divider />

                  {/* Variable a guardar */}
                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="subtitle2" sx={{ color: "#161e55" }}>
                        Variable a guardar
                      </Typography>
                      {selectedNode.data?.saveVariable ? (
                        <ButtonSecondary
                          size="small"
                          onClick={() => updateSelectedNodeData("saveVariable", "")}
                        >
                          Limpiar
                        </ButtonSecondary>
                      ) : null}
                    </Stack>
                    <Paper
                      variant="outlined"
                      sx={{ maxHeight: 260, overflow: "auto", borderRadius: "12px", p: 1 }}
                    >
                      <RadioGroup
                        value={selectedNode.data?.saveVariable || ""}
                        onChange={(event) =>
                          updateSelectedNodeData("saveVariable", event.target.value)
                        }
                      >
                        {saveVariableOptions.map((option) => (
                          <FormControlLabel
                            key={option.key}
                            value={option.key}
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2">
                                {option.label}
                              </Typography>
                            }
                            sx={{ ml: 0 }}
                          />
                        ))}
                      </RadioGroup>
                    </Paper>
                    <FormHelperText sx={{ mt: 0.5 }}>
                      Selecciona la variable donde se guardará la respuesta de este paso.
                    </FormHelperText>
                  </Box>

                  <Divider />

                  {/* Acciones */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: "#161e55", mb: 1 }}>
                      Acciones
                    </Typography>
                    {(() => {
                      const currentActions = selectedNode.data?.actions || [];
                      const currentActionKey = currentActions[0] || null;
                      const alternatives = availableActions.filter(
                        (a) => a.key !== currentActionKey
                      );

                      const setSingleAction = (val) => {
                        if (val) updateSelectedNodeData("actions", [val]);
                      };

                      return (
                        <>
                          {currentActions.length === 0 ? (
                            <Stack spacing={1} sx={{ mb: 1 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel id="action-select-label">Agregar acción</InputLabel>
                                <Select
                                  labelId="action-select-label"
                                  label="Agregar acción"
                                  value=""
                                  onChange={(event) => setSingleAction(event.target.value)}
                                >
                                  {availableActions.map((action) => (
                                    <MenuItem key={action.key} value={action.key}>
                                      {action.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Typography variant="body2" color="text.secondary">
                                Sin acciones configuradas. Usa el selector para agregar una.
                              </Typography>
                            </Stack>
                          ) : (
                            <Stack spacing={1}>
                              {(() => {
                                const meta = availableActions.find(
                                  (a) => a.key === currentActionKey
                                );
                                return (
                                  <Chip
                                    key={currentActionKey}
                                    label={meta?.label || currentActionKey}
                                    onDelete={() => updateSelectedNodeData("actions", [])}
                                    deleteIcon={<DeleteIcon />}
                                    sx={{ justifyContent: "space-between" }}
                                  />
                                );
                              })()}
                              {alternatives.length > 0 ? (
                                <FormControl fullWidth size="small">
                                  <InputLabel id="action-change-label">Cambiar acción</InputLabel>
                                  <Select
                                    labelId="action-change-label"
                                    label="Cambiar acción"
                                    value=""
                                    onChange={(event) => setSingleAction(event.target.value)}
                                  >
                                    {alternatives.map((action) => (
                                      <MenuItem key={action.key} value={action.key}>
                                        {action.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : null}
                            </Stack>
                          )}
                          <FormHelperText sx={{ mt: 0.5 }}>
                            Solo se permite una acción por paso.
                          </FormHelperText>
                        </>
                      );
                    })()}
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="h6" sx={{ color: "#161e55", fontWeight: 700 }}>
                    Sin paso seleccionado
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Selecciona un nodo del flujo para editarlo o una conexión para eliminarla.
                  </Typography>
                </Stack>
              )}
            </Panel>
            </Box>
          </Box>
      </Stack>

      <Dialog open={jsonDialogOpen} onClose={() => setJsonDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Payload del flujo</DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: "12px",
              p: 2,
              overflow: "auto",
              maxHeight: "60vh",
              fontSize: "12px",
            }}
          >
            {JSON.stringify(flowPayload, null, 2)}
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    <Toolbar />
    <Backdrop
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        color: "#161e55",
        backgroundColor: "rgba(255, 255, 255, 0.78)",
      }}
      open={saving}
    >
      <CircularProgress color="inherit" sx={{ color: "#161e55" }} />
    </Backdrop>
    </React.Fragment>
  );
}
